(() => {
  const doc = document.documentElement;
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const pageWrapper = document.querySelector('.page-wrapper');
  const progress = document.querySelector('.awk-motion-progress__bar');
  const scrollTopButton = document.querySelector('[data-awk-scroll-top]');
  const mobileViewportQuery = window.matchMedia('(max-width: 990px), (hover: none) and (pointer: coarse)');
  const finePointerQuery = window.matchMedia('(pointer: fine)');

  let headerScrolled = false;
  let scrollTopVisible = false;
  let floatingOffset = 0;

  doc.classList.add('awk-motion-ready');

  const isJudgeMeRegion = (element) => {
    if (!(element instanceof Element)) return false;
    return element.matches('#judgeme_product_reviews, .jdgm-widget, .jdgm-horizon-widget')
      || Boolean(element.closest('#judgeme_product_reviews, .jdgm-widget, .jdgm-horizon-widget'))
      || Boolean(element.querySelector('#judgeme_product_reviews, .jdgm-widget'));
  };

  const setRevealDelays = (root = document) => {
    root.querySelectorAll('[data-awk-stagger]').forEach((group) => {
      [...group.children].forEach((item, index) => {
        if (isJudgeMeRegion(item)) {
          item.classList.add('is-visible');
          return;
        }
        if (!item.hasAttribute('data-awk-reveal')) item.setAttribute('data-awk-reveal', '');
        item.style.setProperty('--awk-delay', `${Math.min(index * 90, 540)}ms`);
      });
    });
  };

  let revealObserver;
  const observeReveals = (root = document) => {
    setRevealDelays(root);
    const items = [...root.querySelectorAll('[data-awk-reveal]:not(.is-visible)')];
    const judgeMeItems = items.filter(isJudgeMeRegion);
    const animatedItems = items.filter((item) => !isJudgeMeRegion(item));

    judgeMeItems.forEach((item) => item.classList.add('is-visible'));

    if (reduced || !('IntersectionObserver' in window)) {
      animatedItems.forEach((item) => item.classList.add('is-visible'));
      return;
    }
    if (!revealObserver) {
      revealObserver = new IntersectionObserver((entries, observer) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    }
    animatedItems.forEach((item) => revealObserver.observe(item));
  };

  const getScrollMetrics = () => {
    const visualViewportHeight = window.visualViewport?.height || window.innerHeight || document.documentElement.clientHeight;
    const windowTop = Math.max(
      window.scrollY || 0,
      document.documentElement.scrollTop || 0,
      document.body.scrollTop || 0
    );
    const windowHeight = Math.max(
      document.documentElement.scrollHeight || 0,
      document.body.scrollHeight || 0
    );
    const windowMax = Math.max(windowHeight - visualViewportHeight, 0);
    const wrapperTop = pageWrapper?.scrollTop || 0;
    const wrapperViewport = pageWrapper?.clientHeight || visualViewportHeight;
    const wrapperMax = pageWrapper
      ? Math.max(pageWrapper.scrollHeight - wrapperViewport, 0)
      : 0;
    const windowRatio = windowMax > 0 ? Math.min(Math.max(windowTop / windowMax, 0), 1) : 0;
    const wrapperRatio = wrapperMax > 0 ? Math.min(Math.max(wrapperTop / wrapperMax, 0), 1) : 0;
    const usesWrapper = wrapperMax > 0 && wrapperRatio >= windowRatio;

    return {
      wrapper: pageWrapper,
      top: usesWrapper ? wrapperTop : windowTop,
      max: usesWrapper ? wrapperMax : windowMax,
      viewport: visualViewportHeight,
      ratio: Math.max(windowRatio, wrapperRatio)
    };
  };

  const getFloatingOffset = (metrics) => {
    if (!scrollTopButton) return floatingOffset;
    const previewBarHeight = document.getElementById('PBarNextFrameWrapper')?.getBoundingClientRect().height || 0;
    const baseOffset = mobileViewportQuery.matches ? 96 : 48;
    const previewOffset = previewBarHeight > 0
      ? previewBarHeight + (mobileViewportQuery.matches ? 44 : 30)
      : baseOffset;
    const stickyBuy = document.querySelector('[data-awk-sticky-buy].is-visible');
    const stickyOffset = stickyBuy
      ? Math.max(metrics.viewport, window.innerHeight || 0)
        - stickyBuy.getBoundingClientRect().top
        + (mobileViewportQuery.matches ? 42 : 36)
      : baseOffset;
    return Math.ceil(Math.max(baseOffset, previewOffset, stickyOffset));
  };

  const updateScrollState = (metrics) => {
    const ratio = metrics.ratio;

    if (progress) progress.style.transform = `scaleX(${ratio})`;

    const shouldSetHeaderScrolled = metrics.top > 36;
    if (shouldSetHeaderScrolled !== headerScrolled) {
      headerScrolled = shouldSetHeaderScrolled;
      doc.classList.toggle('awk-header-scrolled', headerScrolled);
    }

    if (!scrollTopButton) return;

    const shouldShowScrollTop = (metrics.max > 0 || ratio > 0) && ratio >= 0.5;
    if (shouldShowScrollTop === scrollTopVisible) return;

    scrollTopVisible = shouldShowScrollTop;
    scrollTopButton.classList.toggle('is-visible', scrollTopVisible);
    scrollTopButton.setAttribute('aria-hidden', scrollTopVisible ? 'false' : 'true');
    scrollTopButton.setAttribute('tabindex', scrollTopVisible ? '0' : '-1');
  };

  const initScrollToTop = () => {
    if (!scrollTopButton || scrollTopButton.dataset.awkScrollTopReady === 'true') return;
    scrollTopButton.dataset.awkScrollTopReady = 'true';

    scrollTopButton.addEventListener('click', () => {
      const metrics = getScrollMetrics();
      const behavior = reduced ? 'auto' : 'smooth';
      const scrollElement = (element) => {
        if (!element) return;
        try {
          element.scrollTo({ top: 0, behavior });
        } catch (error) {
          element.scrollTop = 0;
        }
      };

      scrollElement(metrics.wrapper);
      scrollElement(document.scrollingElement);
      window.scrollTo({ top: 0, behavior });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    });
  };

  const canUseInnerScroller = (element) => {
    if (!(element instanceof Element)) return false;
    const style = window.getComputedStyle(element);
    const overflowAllowsScroll = /^(auto|scroll|overlay)$/.test(style.overflowY);
    return overflowAllowsScroll && element.scrollHeight > element.clientHeight + 2;
  };

  const jumpWithoutSmoothScroll = (scroller, top) => {
    if (!(scroller instanceof Element)) return;

    const previousValue = scroller.style.getPropertyValue('scroll-behavior');
    const previousPriority = scroller.style.getPropertyPriority('scroll-behavior');
    scroller.style.setProperty('scroll-behavior', 'auto', 'important');
    scroller.getBoundingClientRect();
    scroller.scrollTop = top;

    if (scroller === document.scrollingElement) {
      window.scrollTo({ left: 0, top, behavior: 'auto' });
    }

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        if (previousValue) {
          scroller.style.setProperty('scroll-behavior', previousValue, previousPriority);
        } else {
          scroller.style.removeProperty('scroll-behavior');
        }
      });
    });
  };

  const moveToReviewWidget = (reviewWidget, source) => {
    if (!(reviewWidget instanceof Element)) return;
    doc.dataset.awkJudgeMeLastEvent = source;

    window.requestAnimationFrame(() => {
      const offset = mobileViewportQuery.matches ? 86 : 96;
      const useInnerScroller = canUseInnerScroller(pageWrapper);
      const scroller = useInnerScroller
        ? pageWrapper
        : document.scrollingElement || document.documentElement;
      if (!(scroller instanceof Element)) return;

      const widgetRect = reviewWidget.getBoundingClientRect();
      const targetTop = useInnerScroller
        ? Math.max(
          pageWrapper.scrollTop
            + widgetRect.top
            - pageWrapper.getBoundingClientRect().top
            - offset,
          0
        )
        : Math.max(
          Math.max(
            window.scrollY || 0,
            document.documentElement.scrollTop || 0,
            document.body.scrollTop || 0
          ) + widgetRect.top - offset,
          0
        );

      doc.dataset.awkJudgeMeScrollMode = useInnerScroller ? 'wrapper' : 'window';
      jumpWithoutSmoothScroll(scroller, targetTop);

      reviewWidget.style.scrollMarginTop = `${offset}px`;
      reviewWidget.classList.add('is-visible');
      reviewWidget.closest('.jdgm-horizon-widget')?.classList.add('is-visible');
      refreshLayoutState();
    });
  };

  const initJudgeMeBadgeLinks = (root = document) => {
    root.querySelectorAll('.awk-ap__buy-card .jdgm-preview-badge').forEach((badge) => {
      if (badge.dataset.awkNativeReviewLink === 'true') return;

      const wrapper = document.createElement('div');
      const link = document.createElement('a');

      wrapper.className = 'awk-judge-me-native-link-wrap';
      wrapper.style.position = 'relative';

      link.className = 'awk-judge-me-native-link';
      link.href = '#judgeme_product_reviews';
      link.setAttribute('aria-label', 'Zu den Kundenbewertungen springen');
      link.setAttribute('title', 'Kundenbewertungen anzeigen');
      link.style.position = 'absolute';
      link.style.inset = '0';
      link.style.zIndex = '3';
      link.style.display = 'block';
      link.style.cursor = 'pointer';
      link.style.borderRadius = '8px';

      link.addEventListener('click', (event) => {
        const reviewWidget = document.getElementById('judgeme_product_reviews');
        if (!reviewWidget) return;
        event.preventDefault();
        event.stopPropagation();
        moveToReviewWidget(reviewWidget, 'native-link');
      });

      badge.before(wrapper);
      wrapper.append(badge, link);
      badge.dataset.awkNativeReviewLink = 'true';
    });
  };

  const initJudgeMeScrollFix = () => {
    if (doc.dataset.awkJudgeMeScrollReady === 'true') return;
    doc.dataset.awkJudgeMeScrollReady = 'true';

    const triggerSelector = '.awk-ap__buy-card .jdgm-preview-badge';
    const findTrigger = (event) => {
      const path = typeof event.composedPath === 'function'
        ? event.composedPath()
        : [event.target];

      for (const node of path) {
        if (!node || typeof node.closest !== 'function') continue;
        const trigger = node.closest(triggerSelector);
        if (trigger) return trigger;
      }
      return null;
    };

    let lastTouchHandledAt = 0;
    const scrollToReviews = (event) => {
      const trigger = findTrigger(event);
      const reviewWidget = document.getElementById('judgeme_product_reviews');
      if (!trigger || !reviewWidget) return;

      if (event.type === 'pointerup' && event.pointerType === 'mouse') return;

      const now = window.performance.now();
      if (event.type === 'click' && now - lastTouchHandledAt < 800) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }
      if (event.type === 'pointerup' || event.type === 'touchend') {
        lastTouchHandledAt = now;
      }

      event.preventDefault();
      event.stopImmediatePropagation();
      moveToReviewWidget(reviewWidget, event.type);
    };

    doc.dataset.awkJudgeMeListenerTarget = 'window';
    window.addEventListener('click', scrollToReviews, true);
    if ('PointerEvent' in window) {
      window.addEventListener('pointerup', scrollToReviews, true);
    } else {
      window.addEventListener('touchend', scrollToReviews, { capture: true, passive: false });
    }
    window.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      scrollToReviews(event);
    }, true);
  };

  // Bind before Judge.me's deferred scripts can register their own scroll handler.
  initJudgeMeBadgeLinks();
  initJudgeMeScrollFix();

  let scrollTicking = false;
  let floatingOffsetDirty = true;
  const requestScrollUpdate = () => {
    if (scrollTicking) return;
    scrollTicking = true;
    window.requestAnimationFrame(() => {
      const metrics = getScrollMetrics();
      const nextOffset = floatingOffsetDirty ? getFloatingOffset(metrics) : floatingOffset;
      floatingOffsetDirty = false;
      updateScrollState(metrics);
      if (scrollTopButton && nextOffset !== floatingOffset) {
        floatingOffset = nextOffset;
        scrollTopButton.style.setProperty('--awk-scroll-top-bottom', `${floatingOffset}px`);
      }
      scrollTicking = false;
    });
  };

  const refreshLayoutState = () => {
    floatingOffsetDirty = true;
    requestScrollUpdate();
  };

  window.addEventListener('scroll', requestScrollUpdate, { passive: true });
  window.addEventListener('resize', refreshLayoutState, { passive: true });
  window.addEventListener('orientationchange', refreshLayoutState, { passive: true });
  window.addEventListener('awk:sticky-buy-change', refreshLayoutState);
  pageWrapper?.addEventListener('scroll', requestScrollUpdate, { passive: true });
  window.visualViewport?.addEventListener('scroll', requestScrollUpdate, { passive: true });
  window.visualViewport?.addEventListener('resize', refreshLayoutState, { passive: true });
  mobileViewportQuery.addEventListener('change', refreshLayoutState);

  const initHeroMotion = (root = document) => {
    if (reduced || !finePointerQuery.matches) return;
    root.querySelectorAll('.awk-motion-hero__stage:not([data-awk-motion-bound])').forEach((stage) => {
      stage.setAttribute('data-awk-motion-bound', 'true');
      let stageRect;
      let pendingPointer;
      let pointerFrame = 0;

      const refreshStageRect = () => {
        stageRect = stage.getBoundingClientRect();
      };

      const updatePointerPosition = () => {
        pointerFrame = 0;
        if (!pendingPointer || !stageRect?.width || !stageRect?.height) return;

        const x = (pendingPointer.clientX - stageRect.left) / stageRect.width;
        const y = (pendingPointer.clientY - stageRect.top) / stageRect.height;
        stage.style.setProperty('--awk-parallax-x', `${(x - .5) * 18}px`);
        stage.style.setProperty('--awk-parallax-y', `${(y - .5) * 18}px`);
        stage.style.setProperty('--awk-pointer-x', `${x * 100}%`);
        stage.style.setProperty('--awk-pointer-y', `${y * 100}%`);
      };

      stage.addEventListener('pointerenter', refreshStageRect, { passive: true });
      stage.addEventListener('pointermove', (event) => {
        pendingPointer = { clientX: event.clientX, clientY: event.clientY };
        if (pointerFrame) return;
        pointerFrame = window.requestAnimationFrame(updatePointerPosition);
      }, { passive: true });
      stage.addEventListener('pointerleave', () => {
        if (pointerFrame) window.cancelAnimationFrame(pointerFrame);
        pointerFrame = 0;
        pendingPointer = undefined;
        stageRect = undefined;
        stage.style.setProperty('--awk-parallax-x', '0px');
        stage.style.setProperty('--awk-parallax-y', '0px');
        stage.style.setProperty('--awk-pointer-x', '50%');
        stage.style.setProperty('--awk-pointer-y', '45%');
      });
      window.addEventListener('resize', () => {
        stageRect = undefined;
      }, { passive: true });
    });
  };

  const initMegaMenu = (root = document) => {
    root.querySelectorAll('[data-awk-mega-root]:not([data-awk-mega-bound])').forEach((mega) => {
      mega.setAttribute('data-awk-mega-bound', 'true');

      const triggers = [...mega.querySelectorAll('[data-awk-mega-trigger]')];
      const panels = [...mega.querySelectorAll('[data-awk-mega-panel]')];
      const menuBar = mega.querySelector('.awk-mega__bar');
      const overlay = mega.querySelector('[data-awk-mega-overlay]');
      const mobileToggle = mega.querySelector('[data-awk-mobile-menu-toggle]');
      const mobileDrawer = mega.querySelector('[data-awk-mobile-menu-drawer]');
      const mobileClose = mega.querySelector('[data-awk-mobile-menu-close]');
      let closeTimer;

      const hydrateDeferredImages = (container) => {
        container?.querySelectorAll('img[data-awk-src]').forEach((image) => {
          image.src = image.dataset.awkSrc;
          image.removeAttribute('data-awk-src');
        });
      };

      const cloneResponsiveImage = (sourceImage, sizes) => {
        if (!(sourceImage instanceof HTMLImageElement)) return null;
        const image = sourceImage.cloneNode(true);
        image.loading = 'lazy';
        image.fetchPriority = 'low';
        image.decoding = 'async';
        image.sizes = sizes;
        return image;
      };

      const hydrateMobilePanel = (panel) => {
        if (!panel || panel.dataset.awkMobileHydrated === 'true') return;

        const featureTarget = panel.querySelector('[data-awk-mobile-feature]');
        if (featureTarget && !featureTarget.querySelector('img')) {
          const key = featureTarget.getAttribute('data-awk-mobile-feature');
          const featureSource = mega.querySelector('[data-awk-mega-feature-source="' + key + '"]');
          const featureImage = cloneResponsiveImage(
            featureSource?.querySelector('img'),
            'calc(100vw - 36px)'
          );
          if (featureImage) featureTarget.prepend(featureImage);
        }

        const productsTarget = panel.querySelector('[data-awk-mobile-products]');
        if (productsTarget && productsTarget.childElementCount === 0) {
          const key = productsTarget.getAttribute('data-awk-mobile-products');
          const productsSource = mega.querySelector('[data-awk-mega-products-source="' + key + '"]');
          const fragment = document.createDocumentFragment();

          productsSource?.querySelectorAll('.awk-mega-product').forEach((sourceCard) => {
            const card = document.createElement('a');
            card.href = sourceCard.href;

            const image = cloneResponsiveImage(
              sourceCard.querySelector('.awk-mega-product__media img'),
              'calc((100vw - 52px) / 3)'
            );
            if (image) card.append(image);

            const title = document.createElement('span');
            title.textContent = sourceCard.querySelector('.awk-mega-product__copy strong')?.textContent?.trim() || '';
            card.append(title);

            const price = document.createElement('small');
            price.textContent = sourceCard.querySelector('.awk-mega-product__copy em')?.textContent?.trim() || '';
            card.append(price);

            fragment.append(card);
          });

          productsTarget.replaceChildren(fragment);
        }

        hydrateDeferredImages(panel);
        panel.dataset.awkMobileHydrated = 'true';
      };

      const closeDesktop = () => {
        window.clearTimeout(closeTimer);
        triggers.forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
        panels.forEach((panel) => {
          panel.classList.remove('is-open');
          panel.setAttribute('aria-hidden', 'true');
        });
        mega.classList.remove('is-desktop-open');
      };

      const scheduleDesktopClose = () => {
        window.clearTimeout(closeTimer);
        closeTimer = window.setTimeout(() => {
          const barHovered = menuBar?.matches(':hover');
          const panelHovered = panels.some((panel) => panel.matches(':hover'));
          if (!barHovered && !panelHovered) closeDesktop();
        }, 170);
      };

      const keepDesktopOpen = () => window.clearTimeout(closeTimer);

      const openDesktop = (trigger) => {
        const targetId = trigger.getAttribute('data-awk-mega-trigger');
        const target = panels.find((panel) => panel.id === targetId);
        if (!target) return;
        hydrateDeferredImages(target);
        window.clearTimeout(closeTimer);
        triggers.forEach((item) => item.setAttribute('aria-expanded', String(item === trigger)));
        panels.forEach((panel) => {
          const active = panel === target;
          panel.classList.toggle('is-open', active);
          panel.setAttribute('aria-hidden', String(!active));
        });
        mega.classList.add('is-desktop-open');
      };

      panels.forEach((panel) => panel.setAttribute('aria-hidden', 'true'));
      triggers.forEach((trigger) => {
        trigger.addEventListener('pointerenter', () => openDesktop(trigger));
        trigger.addEventListener('focus', () => openDesktop(trigger));
        trigger.addEventListener('click', () => openDesktop(trigger));
      });
      menuBar?.addEventListener('pointerenter', keepDesktopOpen);
      menuBar?.addEventListener('pointerleave', scheduleDesktopClose);
      panels.forEach((panel) => {
        panel.addEventListener('pointerenter', keepDesktopOpen);
        panel.addEventListener('pointerleave', scheduleDesktopClose);
      });
      overlay?.addEventListener('click', closeDesktop);

      const closeMobile = () => {
        mobileToggle?.setAttribute('aria-expanded', 'false');
        mobileDrawer?.classList.remove('is-open');
        mobileDrawer?.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('awk-mobile-menu-open');
      };

      const openMobile = () => {
        closeDesktop();
        mobileToggle?.setAttribute('aria-expanded', 'true');
        mobileDrawer?.classList.add('is-open');
        mobileDrawer?.setAttribute('aria-hidden', 'false');
        document.body.classList.add('awk-mobile-menu-open');
      };

      mobileToggle?.addEventListener('click', () => {
        if (mobileDrawer?.classList.contains('is-open')) closeMobile();
        else openMobile();
      });
      mobileClose?.addEventListener('click', closeMobile);

      mega.querySelectorAll('[data-awk-mobile-accordion]').forEach((button) => {
        const panel = document.getElementById(button.getAttribute('aria-controls'));
        if (!panel) return;
        panel.setAttribute('aria-hidden', 'true');
        button.addEventListener('click', () => {
          const willOpen = button.getAttribute('aria-expanded') !== 'true';
          if (willOpen) hydrateMobilePanel(panel);
          button.setAttribute('aria-expanded', String(willOpen));
          panel.classList.toggle('is-open', willOpen);
          panel.setAttribute('aria-hidden', String(!willOpen));
        });
      });

      mobileDrawer?.addEventListener('click', (event) => {
        if (event.target instanceof Element && event.target.closest('a')) closeMobile();
      });
      document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        closeDesktop();
        closeMobile();
      });
      window.matchMedia('(min-width: 991px)').addEventListener('change', closeMobile);
    });
  };

  const init = (root = document) => {
    initMegaMenu(root);
    initJudgeMeBadgeLinks(root);
    initScrollToTop();
    initJudgeMeScrollFix();

    const enhance = () => {
      observeReveals(root);
      initHeroMotion(root);
    };

    if (root === document && 'requestIdleCallback' in window) {
      window.requestIdleCallback(enhance, { timeout: 700 });
    } else if (root === document) {
      window.setTimeout(enhance, 60);
    } else {
      enhance();
    }

    if (root !== document) {
      refreshLayoutState();
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
